import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  Bot, X, Send, Loader2, MessageSquare, Sparkles, 
  CheckCircle2, AlertCircle, Trash2, ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

// Create a global event emitter for opening assistant with project context
export const openAIAssistantWithProject = (projectId, projectName) => {
  window.dispatchEvent(new CustomEvent('open-ai-assistant', { 
    detail: { projectId, projectName } 
  }));
};

const AIAssistant = () => {
  // Persist open state in localStorage
  const [isOpen, setIsOpen] = useState(() => {
    return localStorage.getItem('ai-assistant-open') === 'true';
  });
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem('ai-assistant-session') || null;
  });
  const [pendingAction, setPendingAction] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [focusProject, setFocusProject] = useState(null); // { id, name }
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Listen for external open events with project context
  useEffect(() => {
    const handleOpenWithProject = (event) => {
      const { projectId, projectName } = event.detail;
      
      if (projectId && projectName) {
        // Open with specific project context
        setFocusProject({ id: projectId, name: projectName });
        setMessages([]);
        setSessionId(null);
        localStorage.removeItem('ai-assistant-session');
      } else {
        // Open without project context (general mode)
        setFocusProject(null);
      }
      setIsOpen(true);
    };

    window.addEventListener('open-ai-assistant', handleOpenWithProject);
    return () => window.removeEventListener('open-ai-assistant', handleOpenWithProject);
  }, []);

  // Save open state to localStorage
  useEffect(() => {
    localStorage.setItem('ai-assistant-open', isOpen.toString());
  }, [isOpen]);

  // Save session ID to localStorage
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('ai-assistant-session', sessionId);
    }
  }, [sessionId]);

  // Load session messages when component mounts or session changes
  useEffect(() => {
    if (isOpen && sessionId) {
      loadSession(sessionId);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load sessions on open
  useEffect(() => {
    if (isOpen) {
      loadSessions();
    }
  }, [isOpen]);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/ai-assistant/sessions`);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadSession = async (sid) => {
    try {
      const response = await axios.get(`${API}/ai-assistant/history/${sid}`);
      setMessages(response.data.messages || []);
      setSessionId(sid);
    } catch (error) {
      console.error('Failed to load session:', error);
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
    setPendingAction(null);
    setFocusProject(null);
    localStorage.removeItem('ai-assistant-session');
  };

  const deleteSession = async (sid) => {
    try {
      await axios.delete(`${API}/ai-assistant/sessions/${sid}`);
      setSessions(sessions.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        startNewSession();
      }
      toast.success('Chat gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setPendingAction(null);

    try {
      const response = await axios.post(`${API}/ai-assistant/chat`, {
        message: inputValue,
        session_id: sessionId,
        project_id: focusProject?.id || null
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        action: response.data.action_taken
      };

      setMessages(prev => [...prev, assistantMessage]);
      setSessionId(response.data.session_id);

      if (response.data.action_taken) {
        setPendingAction(response.data.action_taken);
      }

      // Refresh sessions
      loadSessions();
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const executeAction = async () => {
    if (!pendingAction || !sessionId) return;

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/ai-assistant/execute-action`, {
        action_type: pendingAction.action,
        params: pendingAction.params,
        session_id: sessionId
      });

      toast.success(response.data.message);
      
      const confirmMessage = {
        role: 'assistant',
        content: `✅ ${response.data.message}`,
        timestamp: new Date().toISOString(),
        isConfirmation: true
      };
      setMessages(prev => [...prev, confirmMessage]);
      setPendingAction(null);
    } catch (error) {
      toast.error('Aktion fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getActionLabel = (action) => {
    switch (action?.action) {
      case 'create_task': return 'Task erstellen';
      case 'complete_task': return 'Task abschliessen';
      case 'assign_task': return 'Task zuweisen';
      default: return 'Aktion ausführen';
    }
  };

  // Quick questions - context-aware (project-specific when focused)
  const quickQuestions = focusProject ? [
    { label: 'Projektstatus', question: `Wie ist der Status von ${focusProject.name}?` },
    { label: 'Offene Tasks', question: `Welche Tasks sind in ${focusProject.name} noch offen?` },
    { label: 'Risiken', question: `Welche Risiken gibt es in ${focusProject.name}?` },
    { label: 'Team', question: `Wer arbeitet an ${focusProject.name} und wie ist die Auslastung?` },
  ] : [
    { label: 'Projektstatus', question: 'Wie ist der Status meiner Projekte?' },
    { label: 'Offene Tasks', question: 'Welche Tasks sind noch offen?' },
    { label: 'Risiken', question: 'Welche kritischen Risiken gibt es?' },
    { label: 'Team-Auslastung', question: 'Wie ist die Team-Auslastung?' },
  ];

  const handleQuickQuestion = (question) => {
    setInputValue(question);
    // Trigger send after a brief delay to show the question
    setTimeout(() => {
      sendMessageDirect(question);
    }, 100);
  };

  const sendMessageDirect = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setPendingAction(null);

    try {
      const response = await axios.post(`${API}/ai-assistant/chat`, {
        message: messageText,
        session_id: sessionId,
        project_id: focusProject?.id || null
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        action: response.data.action_taken
      };

      setMessages(prev => [...prev, assistantMessage]);
      setSessionId(response.data.session_id);

      if (response.data.action_taken) {
        setPendingAction(response.data.action_taken);
      }

      // Refresh sessions
      loadSessions();
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button - positioned higher to avoid "Made with Emergent" badge */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 right-6 w-14 h-14 rounded-full shadow-lg z-50 transition-all duration-300 ${
          isOpen ? 'bg-destructive hover:bg-destructive/90' : 'bg-primary hover:bg-primary/90'
        }`}
        size="icon"
        data-testid="ai-assistant-button"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <Bot className="h-6 w-6" />
        )}
      </Button>

      {/* Chat Window - also positioned higher */}
      {isOpen && (
        <Card className="fixed bottom-36 right-6 w-96 h-[500px] shadow-2xl z-50 flex flex-col border-2" data-testid="ai-assistant-chat">
          <CardHeader className="pb-2 border-b bg-gradient-to-r from-primary/10 to-primary/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-sm font-semibold">KI-Projektassistent</CardTitle>
                  {focusProject ? (
                    <p className="text-xs text-primary font-medium">📁 {focusProject.name}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Fragen Sie mich alles</p>
                  )}
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={startNewSession}>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Neuer Chat
                  </DropdownMenuItem>
                  {sessions.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs text-muted-foreground">Letzte Chats</div>
                      {sessions.slice(0, 5).map(s => (
                        <DropdownMenuItem 
                          key={s.session_id} 
                          onClick={() => loadSession(s.session_id)}
                          className="flex justify-between items-center"
                        >
                          <span className="truncate text-xs">{s.preview || 'Chat'}</span>
                          <Trash2 
                            className="w-3 h-3 text-muted-foreground hover:text-destructive ml-2 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSession(s.session_id);
                            }}
                          />
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>

          <CardContent className="flex-1 p-0 flex flex-col overflow-hidden">
            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground py-8">
                  <Bot className="w-12 h-12 mb-4 opacity-50" />
                  <p className="text-sm font-medium">Hallo! Ich bin Ihr KI-Projektassistent.</p>
                  <p className="text-xs mt-2">Klicken Sie auf ein Thema:</p>
                  <div className="flex flex-wrap gap-2 mt-3 justify-center">
                    {quickQuestions.map((q, idx) => (
                      <Badge 
                        key={idx}
                        variant="secondary" 
                        className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                        onClick={() => handleQuickQuestion(q.question)}
                      >
                        {q.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                          msg.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : msg.isError
                            ? 'bg-destructive/10 text-destructive border border-destructive/20'
                            : msg.isConfirmation
                            ? 'bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {msg.action && (
                          <div className="mt-2 pt-2 border-t border-border/50">
                            <p className="text-xs opacity-70 mb-1">Vorgeschlagene Aktion:</p>
                            <Badge variant="outline" className="text-xs">
                              {getActionLabel(msg.action)}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Pending Action */}
            {pendingAction && (
              <div className="px-4 py-2 bg-amber-500/10 border-t border-amber-500/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    <span className="text-xs text-amber-700 dark:text-amber-400">
                      {getActionLabel(pendingAction)}?
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-6 text-xs"
                      onClick={() => setPendingAction(null)}
                    >
                      Abbrechen
                    </Button>
                    <Button 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={executeAction}
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                      Ausführen
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-3 border-t bg-background">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Nachricht eingeben..."
                  disabled={isLoading}
                  className="flex-1 text-sm"
                  data-testid="ai-assistant-input"
                />
                <Button 
                  onClick={sendMessage} 
                  disabled={isLoading || !inputValue.trim()}
                  size="icon"
                  data-testid="ai-assistant-send"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default AIAssistant;
